<script setup>
import { computed, reactive, shallowRef } from 'vue';
import SettingsSection from './SettingsSection.vue';
import TeachingListSection from './TeachingListSection.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import { apiRequest, errorMessage } from '@/api/http';
import { useTeachingList } from '@/composables/useTeachingList';
import { useToast } from '@/composables/useToast';
import { SchoolLevel } from '@/constants/accounts';

// Settings → Teaching (faculty): the subjects and grade levels the teacher
// can be scheduled for, and what their Form 29 prints as their area of
// specialization and assignments. Each part saves on its own.
const toast = useToast();
const subjects = useTeachingList('subjects');
const grades = useTeachingList('grades');
const loading = shallowRef(true);
const level = shallowRef(null);
const subjectsDescription = computed(() =>
  level.value === SchoolLevel.Junior
    ? 'Subjects you can be scheduled to teach. Junior High accounts pick from Junior High subjects.'
    : level.value === SchoolLevel.Senior
      ? 'Subjects you can be scheduled to teach. Senior High accounts pick from Senior High subjects.'
      : 'Subjects you can be scheduled to teach.',
);
const gradesDescription = computed(() =>
  level.value === SchoolLevel.Junior
    ? 'Grade levels you handle. Junior High accounts pick from Grades 7–10.'
    : level.value === SchoolLevel.Senior
      ? 'Grade levels you handle. Senior High accounts pick from Grades 11–12.'
      : 'Grade levels you handle.',
);

// Form 29 details (`department` is the Area of Specialization,
// `special_assignments` the Ancillary Assignments).
const program = reactive({ department: '', teaching_related_assignments: '', special_assignments: '' });
const programSaved = shallowRef({ ...program });
const programDirty = computed(() =>
  Object.keys(program).some((k) => program[k] !== programSaved.value[k]),
);
const programSaving = shallowRef(false);
const programSavedAt = shallowRef(0);

async function saveProgram() {
  programSaving.value = true;
  try {
    await apiRequest('patch', '/user-details/my-form-details', { ...program });
    programSaved.value = { ...program };
    programSavedAt.value = Date.now();
  } catch (e) {
    toast.error(errorMessage(e, 'Could not save your class program details.'));
  } finally {
    programSaving.value = false;
  }
}

apiRequest('get', '/user-details/getPersonalInfo')
  .then((info) => {
    program.department = info.department ?? '';
    program.teaching_related_assignments = info.teaching_related_assignments ?? '';
    program.special_assignments = info.special_assignments ?? '';
    programSaved.value = { ...program };
    level.value = info.levelScope ?? null;
    return Promise.all([subjects.load(info.id, info.levelScope), grades.load(info.id, info.levelScope)]);
  })
  .catch((e) => toast.error(errorMessage(e, 'Could not load your teaching details.')))
  .finally(() => (loading.value = false));

async function save(list, what) {
  try {
    await list.save();
  } catch (e) {
    toast.error(errorMessage(e, `Could not save your ${what}.`));
  }
}
</script>

<template>
  <div v-if="loading" class="d-flex justify-center pa-8">
    <v-progress-circular indeterminate size="28" width="3" />
  </div>

  <div v-else class="teaching">
    <TeachingListSection
      title="Subjects"
      :description="subjectsDescription"
      :items="subjects.items.value"
      :available="subjects.available.value"
      add-label="Add a subject…"
      empty-text="You haven't added any subjects yet."
      :dirty="subjects.isDirty.value"
      :saving="subjects.saving.value"
      :saved-at="subjects.savedAt.value"
      @add="subjects.add"
      @remove="subjects.remove"
      @reset="subjects.reset"
      @save="save(subjects, 'subjects')"
    />

    <hr class="teaching__sep" />

    <TeachingListSection
      title="Grade levels"
      :description="gradesDescription"
      :items="grades.items.value"
      :available="grades.available.value"
      add-label="Add a grade level…"
      empty-text="You haven't added any grade levels yet."
      :dirty="grades.isDirty.value"
      :saving="grades.saving.value"
      :saved-at="grades.savedAt.value"
      @add="grades.add"
      @remove="grades.remove"
      @reset="grades.reset"
      @save="save(grades, 'grade levels')"
    />

    <hr class="teaching__sep" />

    <SettingsSection
      title="Form 29 details"
      description="Printed on your Teacher's Individual Teaching Load."
      :dirty="programDirty"
      :saving="programSaving"
      :saved-at="programSavedAt"
      @save="saveProgram"
      @reset="Object.assign(program, programSaved)"
    >
      <div class="form-stack">
        <UiFormField label="Area of Specialization" v-slot="{ id }">
          <v-text-field
            :id="id"
            v-model="program.department"
            maxlength="150"
            placeholder="e.g. TVL - Hilot Wellness Massage / English"
            hide-details="auto"
          />
        </UiFormField>
        <UiFormField label="Teaching Related Assignments" description="Separate several with commas." v-slot="{ id }">
          <v-textarea
            :id="id"
            v-model="program.teaching_related_assignments"
            rows="2"
            auto-grow
            maxlength="500"
            placeholder="e.g. G-11 Adviser Einstein, Aral Tutor"
            hide-details="auto"
          />
        </UiFormField>
        <UiFormField label="Ancillary Assignments" description="Separate several with commas." v-slot="{ id }">
          <v-textarea
            :id="id"
            v-model="program.special_assignments"
            rows="2"
            auto-grow
            maxlength="500"
            placeholder="e.g. Audit Committee School Canteen, SHS Waste Management Coordinator"
            hide-details="auto"
          />
        </UiFormField>
      </div>
    </SettingsSection>
  </div>
</template>

<style scoped>
.teaching {
  display: flex;
  flex-direction: column;
  gap: 32px;
}
.teaching__sep {
  margin: 0;
  border: 0;
  border-top: 1px solid var(--border);
}
</style>
